import { useEffect, useState } from "react";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useCreateUserMutation,
  useDeleteSingleUserMutation,
  useGetUserDetailsQuery,
  useUpdateUserMutation,
} from "../../../store/api/userSlice";
import { getErrorMessage } from "../../../utils";
import { capitalizeFirstLetter } from "../../../utils/dateAndTimeConversion";
import "./userform.scss";
import { useTranslation } from "react-i18next";

const UserForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    email: "",
    role: "",
  });

  const [saveType, setSaveType] = useState("");
  const [errors, setErrors] = useState({});
  // create user query
  const [createUser, { isLoading }] = useCreateUserMutation();
  const [updateUser, { isLoading: isUpdateLoading }] = useUpdateUserMutation();
  // get user details by id
  const {
    data,
    isSuccess,
    isLoading: isGetLoading,
    isError,
    error,
  } = useGetUserDetailsQuery(id, { skip: !id });

  // delete single user
  const [deleteSingleUser, { isLoading: isDeleteLoading }] =
    useDeleteSingleUserMutation();

  if (isError) {
    toast.dismiss();
    toast.error(getErrorMessage(error));
  }

  const validateForm = () => {
    const newErrors = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (!value)
        newErrors[key] = `${t("errors." + key)} ${t("errors.required")}`;
    });
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      newErrors.email = `${t("invalid")} ${t("email")} ${t("format")}`;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    setErrors((prev) => ({
      ...prev,
      [name]: value ? "" : `${t("errors." + name)} ${t("errors.required")}`,
    }));
  };

  const handleSubmit = async (e, key) => {
    e.preventDefault();
    if (!validateForm()) return;
    toast.dismiss();
    setSaveType(key);
    try {
      let response;
      id
        ? (response = await updateUser({ data: formData, id }).unwrap())
        : (response = await createUser(formData).unwrap());

      if (response.statusCode === 201 || response.statusCode === 200) {
        toast.success(response.message);
        key === "save"
          ? navigate("/user-management")
          : setFormData({ email: "", role: "" });
      }
    } catch (err) {
      const mesg = err.data?.message || "Something went wrong";
      toast.error(mesg);
    }
  };

  const handleUserAction = async () => {
    toast.dismiss();
    try {
      let response = await deleteSingleUser(id).unwrap();

      if (response.statusCode === 201 || response.statusCode === 200) {
        toast.success(response.message);
        navigate("/user-management");
      }
    } catch (err) {
      const mesg = err.data?.message || "Something went wrong";
      toast.error(mesg);
    }
  };

  useEffect(() => {
    if (isSuccess && data?.data && id) {
      setFormData({
        email: data?.data.email,
        role: data?.data.role,
      });
    }
  }, [data, isSuccess]);

  return (
    <>
      <div className="adminSchools">
        <div className="d-flex mb-3 px-3 pt-3">
          <button className="page-back-btn" onClick={() => navigate(-1)}>
            ← Back
          </button>
        </div>
        <div className="user-form-container container py-5">
          <div className="row justify-content-between d-flex">
            {isGetLoading ? (
              <div className="h-50 justify-content-between d-flex align-items-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only p-0"></span>
                </div>
              </div>
            ) : (
              <>
                <form onSubmit={(e) => handleSubmit(e, "save")}>
                  <div className="row justify-content-between d-flex">
                    <div className="col-lg-9">
                      <div className="form-box p-4 shadow-sm rounded">
                        <div className="mb-3 row align-items-center">
                          <label className="col-sm-3 col-form-label fw-bold">
                            {`${t("user")} ${t("type")}`}
                            <span className="text-danger">*</span>
                          </label>
                          <div className="col-sm-9">
                            <select
                              className="formSelect user-select bg-white"
                              value={formData.role}
                              onChange={handleChange}
                              name="role"
                            >
                              <option value="">
                                {`${t("select")} ${t("role")}`}
                              </option>
                              <option value="school">{t("school")}</option>
                              <option value="vendor">{t("vendor")}</option>
                              {/* <option value="driver">Driver</option> */}
                              {/* <option value="parent">Parent</option> */}
                            </select>
                            <br />
                            {errors.role && (
                              <span style={{ color: "red" }}>
                                {errors.role}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="mb-3 row align-items-center">
                          <label className="col-sm-3 col-form-label fw-bold">
                            {`${t("email")} ${t("address")}`}{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <div className="col-sm-9">
                            <input
                              type="email"
                              className="formInput user-form bg-white"
                              value={formData.email}
                              onChange={handleChange}
                              name="email"
                              placeholder={`${t("enter")} ${t("email")}`}
                            />{" "}
                            <br />
                            {errors.email && (
                              <span style={{ color: "red" }}>
                                {errors.email}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex col-lg-3 flex-column gap-3 justify-content-center">
                      <button
                        className="btn btn-warning text-white btn-size"
                        disabled={
                          saveType === "save" && (isLoading || isUpdateLoading)
                        }
                        type="submit"
                      >
                        {saveType === "save" &&
                        (isLoading || isUpdateLoading) ? (
                          <div
                            className="spinner-border loaderStyle"
                            role="status"
                          >
                            <span className="sr-only p-0"></span>
                          </div>
                        ) : id ? (
                          t("update")
                        ) : (
                          t("save")
                        )}
                      </button>
                      {/* <button
                      className="btn btn-warning text-white btn-delete btn-size"
                      disabled={isDeleteLoading}
                      onClick={handleUserAction}
                    >
                      Delete
                    </button> */}
                      <button
                        className="btn btn-dark text-white btn-size"
                        disabled={
                          saveType === "saveAndAddOther" &&
                          (isLoading || isUpdateLoading)
                        }
                        onClick={(e) => handleSubmit(e, "saveAndAddOther")}
                      >
                        {saveType === "saveAndAddOther" &&
                        (isLoading || isUpdateLoading) ? (
                          <div
                            className="spinner-border loaderStyle"
                            role="status"
                          >
                            <span className="sr-only p-0"></span>
                          </div>
                        ) : id ? (
                          `${t("update")} ${t("and")} ${t("add")} ${t(
                            "another",
                          )}`
                        ) : (
                          `${t("save")} ${t("and")} ${t("add")} ${t("another")}`
                        )}
                      </button>
                      {id && (
                        <button className="btn btn-warning text-white btn-size">
                          {`${t("resend")} ${t("email")}`}
                        </button>
                      )}
                    </div>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UserForm;
